import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  Megaphone,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Send,
  Trash2,
  Star,
  Eye,
  BarChart3,
} from 'lucide-react'
import { portaisIds } from '../../constants/integracoes'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { SearchInput } from '../../components/ui'
import { Badge, Button, EmptyState } from '../../design-system'

interface Publicacao {
  portal: string
  status: string
  precoWeb: number | null
  destaque: boolean
  tipoDestaque: string | null
  penalizado: boolean
  urlAnuncio: string | null
}

interface VeiculoIntegrador {
  veiculoId: number
  fotoPrincipal: string | null
  descricao: string
  marca: string | null
  modelo: string | null
  versao: string | null
  anoFabricacao: number
  anoModelo: number
  cor: string | null
  km: number
  placa: string | null
  status: string
  valorVenda: number | null
  valorFipe: number | null
  diasEstoque: number
  publicacoes: Publicacao[]
}

interface PagedResult {
  items: VeiculoIntegrador[]
  totalItems: number
  page: number
  pageSize: number
}


export default function PublicacoesGerenciamento() {
  const { showApiError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<PagedResult | null>(null)
  const [selectedVeiculos, setSelectedVeiculos] = useState<number[]>([])
  const [selectedPortais, setSelectedPortais] = useState<string[]>([])
  const [expandedVeiculo, setExpandedVeiculo] = useState<number | null>(null)
  const [publicando, setPublicando] = useState(false)

  const [filtros, setFiltros] = useState({
    busca: '',
    statusVeiculo: '',
    portal: '',
    statusPublicacao: '',
    page: 1,
    pageSize: 20,
  })

  const fetchVeiculos = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.statusVeiculo) params.append('statusVeiculo', filtros.statusVeiculo)
      params.append('page', filtros.page.toString())
      params.append('pageSize', filtros.pageSize.toString())

      const response = await api.get(`/portais/integrador?${params.toString()}`, { signal })
      setData(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }, [filtros, showApiError])

  useEffect(() => {
    const controller = new AbortController()
    fetchVeiculos(controller.signal)
    return () => controller.abort()
  }, [fetchVeiculos])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PUBLICADO':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      case 'ERRO':
        return <XCircle className="w-4 h-4 text-rose-500" />
      case 'AGUARDANDO':
      case 'DESATUALIZADO':
        return <Clock className="w-4 h-4 text-amber-500" />
      default:
        return <Clock className="w-4 h-4 text-slate-400" />
    }
  }

  const getPublicacaoStatus = (veiculo: VeiculoIntegrador, portal: string) => {
    const pub = veiculo.publicacoes.find(p => p.portal === portal)
    if (!pub) return { status: 'NAO_PUBLICADO', destaque: false, penalizado: false, url: null }
    return { status: pub.status, destaque: pub.destaque, penalizado: pub.penalizado, url: pub.urlAnuncio }
  }

  const toggleVeiculoSelection = (veiculoId: number) => {
    setSelectedVeiculos(prev =>
      prev.includes(veiculoId)
        ? prev.filter(id => id !== veiculoId)
        : [...prev, veiculoId]
    )
  }

  const toggleAllVeiculos = () => {
    if (!data) return
    if (selectedVeiculos.length === data.items.length) {
      setSelectedVeiculos([])
    } else {
      setSelectedVeiculos(data.items.map(v => v.veiculoId))
    }
  }

  const togglePortalSelection = (portal: string) => {
    setSelectedPortais(prev =>
      prev.includes(portal)
        ? prev.filter(p => p !== portal)
        : [...prev, portal]
    )
  }

  const publicarEmMassa = async () => {
    if (selectedVeiculos.length === 0) {
      showApiError({ message: 'Selecione ao menos um veículo' })
      return
    }
    if (selectedPortais.length === 0) {
      showApiError({ message: 'Selecione ao menos um portal' })
      return
    }

    try {
      setPublicando(true)
      const response = await api.post('/portais/sincronizar', {
        veiculoIds: selectedVeiculos,
        portais: selectedPortais,
        acao: 'PUBLICAR',
      })

      const result = response.data
      showSuccess(`Publicação concluída: ${result.sucesso} sucesso, ${result.erros} erros`)
      setSelectedVeiculos([])
      setSelectedPortais([])
      fetchVeiculos()
    } catch (error) {
      showApiError(error)
    } finally {
      setPublicando(false)
    }
  }

  const removerEmMassa = async () => {
    if (selectedVeiculos.length === 0) {
      showApiError({ message: 'Selecione ao menos um veículo' })
      return
    }
    if (selectedPortais.length === 0) {
      showApiError({ message: 'Selecione ao menos um portal' })
      return
    }

    if (!window.confirm(`Remover ${selectedVeiculos.length} veículo(s) de ${selectedPortais.length} portal(is)?`)) {
      return
    }

    try {
      setPublicando(true)
      const response = await api.post('/portais/sincronizar', {
        veiculoIds: selectedVeiculos,
        portais: selectedPortais,
        acao: 'REMOVER',
      })

      const result = response.data
      showSuccess(`Remoção concluída: ${result.sucesso} sucesso, ${result.erros} erros`)
      setSelectedVeiculos([])
      setSelectedPortais([])
      fetchVeiculos()
    } catch (error) {
      showApiError(error)
    } finally {
      setPublicando(false)
    }
  }

  const formatCurrency = (value: number | null) => {
    if (!value) return '-'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const totalPages = data ? Math.ceil(data.totalItems / data.pageSize) : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900">
            <Megaphone className="w-7 h-7 text-primary-600" />
            Gerenciar Publicações
          </h1>
          <p className="mt-1 text-slate-500">
            Publique seus veículos nos portais de anúncios
          </p>
        </div>
        <Button variant="secondary" onClick={() => fetchVeiculos()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4">
        <div className="flex flex-wrap gap-4">
          <SearchInput
            value={filtros.busca}
            onChange={(value) => setFiltros({ ...filtros, busca: value, page: 1 })}
            placeholder="Buscar por placa, marca, modelo..."
          />
          <select
            value={filtros.statusVeiculo}
            onChange={(e) => setFiltros({ ...filtros, statusVeiculo: e.target.value, page: 1 })}
            className="px-4 py-2.5 border border-slate-200 rounded-xl text-slate-600 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todos os Status</option>
            <option value="DISPONIVEL">Disponível</option>
            <option value="RESERVADO">Reservado</option>
            <option value="VENDIDO">Vendido</option>
          </select>
        </div>
      </div>

      {/* Seleção de Portais para Ação em Massa */}
      {selectedVeiculos.length > 0 && (
        <div className="rounded-2xl border border-primary-100 bg-primary-50 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-bold text-primary-800">
              {selectedVeiculos.length} veículo(s) selecionado(s)
            </span>
            <button
              onClick={() => setSelectedVeiculos([])}
              className="text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              Limpar seleção
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-slate-600">Portais:</span>
            {portaisIds.map(portal => (
              <button
                key={portal}
                onClick={() => togglePortalSelection(portal)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  selectedPortais.includes(portal)
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
              >
                {portal}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={publicarEmMassa}
              disabled={publicando || selectedPortais.length === 0}
            >
              {publicando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Publicar nos Portais
            </Button>
            <Button
              variant="danger"
              onClick={removerEmMassa}
              disabled={publicando || selectedPortais.length === 0}
            >
              {publicando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remover dos Portais
            </Button>
          </div>
        </div>
      )}

      {/* Lista de Veículos */}
      <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={data?.items.length ? selectedVeiculos.length === data.items.length : false}
                  onChange={toggleAllVeiculos}
                  className="w-4 h-4 text-primary-600 rounded"
                />
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Veículo</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Preço</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Status</th>
              <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-slate-400" colSpan={8}>
                Portais
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-slate-400">Ações</th>
            </tr>
            <tr className="bg-slate-100">
              <th colSpan={4}></th>
              {portaisIds.map(portal => (
                <th key={portal} className="px-2 py-1 text-center text-xs font-semibold text-slate-500">
                  {portal.substring(0, 3)}
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={13} className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td colSpan={13}>
                  <EmptyState>Nenhum veículo encontrado</EmptyState>
                </td>
              </tr>
            ) : (
              data?.items.map(veiculo => (
                <>
                  <tr key={veiculo.veiculoId} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedVeiculos.includes(veiculo.veiculoId)}
                        onChange={() => toggleVeiculoSelection(veiculo.veiculoId)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {veiculo.fotoPrincipal ? (
                          <img
                            src={veiculo.fotoPrincipal}
                            alt={veiculo.descricao}
                            className="w-16 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-12 bg-slate-100 rounded flex items-center justify-center">
                            <Eye className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{veiculo.descricao}</p>
                          <p className="text-xs text-slate-500">
                            {veiculo.placa} | {veiculo.km?.toLocaleString()} km | {veiculo.diasEstoque} dias
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold tabular-nums text-slate-900">
                        {formatCurrency(veiculo.valorVenda)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        tone={
                          veiculo.status === 'DISPONIVEL' ? 'success' :
                          veiculo.status === 'RESERVADO' ? 'warning' :
                          'neutral'
                        }
                      >
                        {veiculo.status}
                      </Badge>
                    </td>
                    {portaisIds.map(portal => {
                      const pubStatus = getPublicacaoStatus(veiculo, portal)
                      return (
                        <td key={portal} className="px-2 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getStatusIcon(pubStatus.status)}
                            {pubStatus.destaque && <Star className="w-3 h-3 text-amber-500" />}
                            {pubStatus.penalizado && <AlertTriangle className="w-3 h-3 text-rose-500" />}
                          </div>
                        </td>
                      )
                    })}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setExpandedVeiculo(expandedVeiculo === veiculo.veiculoId ? null : veiculo.veiculoId)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        {expandedVeiculo === veiculo.veiculoId ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>

                  {/* Detalhes expandidos */}
                  {expandedVeiculo === veiculo.veiculoId && (
                    <tr>
                      <td colSpan={13} className="px-4 py-4 bg-slate-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {portaisIds.map(portal => {
                            const pub = veiculo.publicacoes.find(p => p.portal === portal)
                            return (
                              <div key={portal} className="p-3 rounded-xl bg-white ring-1 ring-slate-100">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <PortalLogo portal={portal} size="sm" />
                                    <span className="font-semibold text-sm text-slate-800">{portal}</span>
                                  </div>
                                  {getStatusIcon(pub?.status || 'NAO_PUBLICADO')}
                                </div>
                                <p className="text-xs text-slate-600">
                                  Status: {pub?.status || 'Não publicado'}
                                </p>
                                {pub?.precoWeb && (
                                  <p className="text-xs text-slate-600">
                                    Preço: {formatCurrency(pub.precoWeb)}
                                  </p>
                                )}
                                {pub?.tipoDestaque && (
                                  <p className="text-xs flex items-center gap-1 text-slate-600">
                                    <Star className="w-3 h-3" />
                                    {pub.tipoDestaque}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  {pub?.urlAnuncio && (
                                    <a
                                      href={pub.urlAnuncio}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      Anúncio
                                    </a>
                                  )}
                                  {pub && (
                                    <Link
                                      to={`/integracoes/publicacoes/${veiculo.veiculoId}/${portal.toLowerCase()}`}
                                      className="text-xs flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700"
                                    >
                                      <BarChart3 className="w-3 h-3" />
                                      Detalhes
                                    </Link>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>

        {/* Paginação */}
        {data && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              Mostrando {((data.page - 1) * data.pageSize) + 1} a {Math.min(data.page * data.pageSize, data.totalItems)} de {data.totalItems}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFiltros({ ...filtros, page: filtros.page - 1 })}
                disabled={filtros.page <= 1}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFiltros({ ...filtros, page: filtros.page + 1 })}
                disabled={filtros.page >= totalPages}
              >
                Próximo
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
